// A sheet on the counterparty's profile: the credit assessment (TX-US-0027) and the KYC review
// (TX-US-0035) are this panel with their own sections, API calls and words. The owner fills the
// sheet in and saves it against the version read; everyone else reads what was answered. The API
// is the authority on who may write - `canEdit` only decides whether the form is offered.
//
// `onSaved` hands the profile the sheet as read and as saved, so the gate's button knows what it
// is still waiting on without a second request. It must be stable - a state setter - or the sheet
// is re-read, and a draft in progress discarded, on every render of the profile. `load`, `save`
// and `kit` must be stable for the same reason: module-level values, not built in a render.
//
// `kit` is the sheet's own module: SECTIONS, draftFrom, bodyFrom, draftErrors, missingLabels.
// `copy` is what the panel says: heading, preparedBy, save, saved, coreKind (the 409 a save
// meets when it would empty a passed gate's core), keeps and stale.

import { useEffect, useState } from 'react';

import { notify } from '../../ui/notify.jsx';
import SheetFields from './SheetFields.jsx';
import SheetView from './SheetView.jsx';

export default function SheetPanel({
  id, counterpartyId, canEdit, onSaved, load, save, kit, copy, children,
}) {
  const [record, setRecord] = useState(null);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    load(counterpartyId)
      .then((found) => {
        if (cancelled) return;
        setRecord(found);
        setDraft(kit.draftFrom(found.sheet));
        onSaved?.(found);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [counterpartyId, onSaved, load, kit]);

  const submit = async (event) => {
    event.preventDefault();
    const refused = kit.draftErrors(draft);
    setErrors(refused);
    if (Object.keys(refused).length > 0) {
      setError('Some rows need attention.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await save(counterpartyId, kit.bodyFrom(draft), record.version);
      setRecord(saved);
      setDraft(kit.draftFrom(saved.sheet));
      onSaved?.(saved);
      notify(copy.saved);
    } catch (err) {
      if (err.detail?.kind === copy.coreKind) {
        setError(`${copy.keeps}: ${kit.missingLabels(err.detail.missing).join(', ')}.`);
      } else if (err.status === 409) {
        setError(copy.stale);
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`sheet-panel ${id}`} aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`}>{copy.heading}</h3>

      {error && <p className="error">{error}</p>}
      {!record && !error && <p className="muted">Loading…</p>}

      {record && record.version > 0 && (
        <p className="hint">
          {copy.preparedBy} {record.updated_by}, {new Date(record.updated_at).toLocaleString()}
        </p>
      )}

      {children}

      {record && !canEdit && <SheetView sections={kit.SECTIONS} sheet={record.sheet} />}

      {record && canEdit && (
        <form className="deal-form credit-sheet" onSubmit={submit}>
          <SheetFields
            sections={kit.SECTIONS}
            draft={draft}
            onChange={setDraft}
            errors={errors}
            waiting={record.missing_for_approval ?? []}
          />
          <div className="form-actions">
            <button type="submit" disabled={busy}>
              {busy ? 'Saving…' : copy.save}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
